import { Protein } from '../types';

/**
 * Strip Unicode combining diacritical marks (accents) from a string.
 * Uses NFD normalization to decompose characters, then removes the marks.
 */
export const stripAccents = (value: string): string => value.normalize('NFD').replace(/[̀-ͯ]/g, '');

/**
 * Normalize a string to a canonical token form: lowercase, no accents,
 * spaces/hyphens replaced with underscores.
 * Used for proteins, pathologies, config keys.
 */
// Invisible format/control characters (zero-width spaces, soft hyphen, bidi
// marks, BOM, etc.). Stripped so a token like "chicken<ZWSP>" cannot survive as
// distinct from its alias -- must stay in sync with the backend normalize_token,
// which drops Unicode categories Cf and Cc.
//
// Assembled from code-point ranges at RUNTIME rather than a static regex
// literal: a literal trips `no-control-regex` in the CRA production build
// (`react-scripts build`), which -- unlike `eslint src` -- does not honor inline
// eslint-disable comments. Keeping the pattern runtime-built avoids the rule in
// both. Ranges preserved verbatim from the original literal.
const INVISIBLE_CHAR_RANGES: ReadonlyArray<readonly [number, number]> = [
  [0x00, 0x1f], // C0 controls
  [0x7f, 0x9f], // DEL + C1 controls
  [0xad, 0xad], // soft hyphen
  [0x61c, 0x61c], // arabic letter mark
  [0x180e, 0x180e], // mongolian vowel separator
  [0x200b, 0x200f], // zero-width space/joiner + LTR/RTL marks
  [0x202a, 0x202e], // bidi embeddings/overrides
  [0x2060, 0x2064], // word joiner + invisible math operators
  [0x2066, 0x206f], // bidi isolates + deprecated format chars
  [0xfeff, 0xfeff], // BOM / zero-width no-break space
  [0xfff9, 0xfffb], // interlinear annotation anchors
];
const INVISIBLE_CHARS = new RegExp(
  '[' +
    INVISIBLE_CHAR_RANGES.map(
      ([start, end]) => `\\u${start.toString(16).padStart(4, '0')}-\\u${end.toString(16).padStart(4, '0')}`,
    ).join('') +
    ']',
  'g',
);

export const normalizeToken = (value: string): string => {
  return stripAccents((value || '').trim().toLowerCase())
    .replace(INVISIBLE_CHARS, '')
    .replace(/[\s-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
};

/**
 * Normalize a string to readable text form: lowercase, no accents,
 * non-alphanumeric replaced with spaces, collapsed.
 * Used for breed names and free-text search.
 */
export const normalizeText = (value: string): string =>
  stripAccents((value || '').toLowerCase())
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Normalize a breed name to a canonical key form: lowercase, no accents,
 * slashes replaced with spaces (then underscored), hyphens/spaces collapsed.
 * Used for breed lookups in config and recommendations.
 */
export const normalizeBreedKey = (name: string): string =>
  stripAccents(name.trim().toLowerCase())
    .replace(/[/\\]+/g, ' ')
    .replace(/[\s-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');

/**
 * Normalize a protein value and resolve it through an alias lookup.
 */
export const normalizeProtein = (value: string, aliasLookup: Record<string, string>, fallback: Protein): Protein => {
  const normalized = normalizeToken(value);
  if (!normalized) return fallback;
  return (aliasLookup[normalized] || normalized) as Protein;
};
